import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Clock, AlertTriangle, Circle, XCircle, Loader, ChevronDown, ChevronRight } from "lucide-react";
import { groundOpsApi } from "@/api";
import AIPredictionPanel from "./AIPredictionPanel";

const STATUS_CONFIG = {
  completed:   { label: "Done",        color: "text-green-400",  bg: "bg-green-500/10 border-green-500/25",   icon: CheckCircle, dot: "bg-green-500"  },
  in_progress: { label: "In Progress", color: "text-blue-400",   bg: "bg-blue-500/10 border-blue-500/25",     icon: Clock,       dot: "bg-blue-400 animate-pulse"   },
  not_started: { label: "Not Started", color: "text-slate-500",  bg: "bg-slate-700/10 border-slate-700/25",  icon: Circle,      dot: "bg-slate-600"  },
  delayed:     { label: "Delayed",     color: "text-red-400",    bg: "bg-red-500/10 border-red-500/30",       icon: AlertTriangle, dot: "bg-red-500 animate-pulse"  },
  blocked:     { label: "Blocked",     color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/30", icon: XCircle,     dot: "bg-purple-500 animate-pulse" },
  escalated:   { label: "Escalated",   color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30", icon: AlertTriangle, dot: "bg-orange-500 animate-pulse" },
};

const RISK_BAR = {
  on_time:  "bg-green-500",
  at_risk:  "bg-amber-500",
  delayed:  "bg-red-500",
  critical: "bg-red-600",
};

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
}
function diffMins(a, b) {
  if (!a || !b) return null;
  return Math.round((new Date(a) - new Date(b)) / 60000);
}

function TaskRow({ task, onUpdate }) {
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.not_started;
  const Icon = cfg.icon;
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const delayMinutes = task.actual_time ? diffMins(task.actual_time, task.planned_time) : null;

  async function handleStatus(newStatus) {
    setSaving(true);
    try {
      await onUpdate(task.id, { status: newStatus });
    } finally { setSaving(false); }
  }

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${cfg.bg}`} data-testid={`task-row-${task.code}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Sequence dot */}
        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-800 border border-slate-600/40 flex-shrink-0">
          <span className="text-[9px] font-mono text-slate-400">{task.sequence}</span>
        </div>

        {/* Task name + critical badge */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white">{task.name}</span>
            {task.is_critical && (
              <span className="text-[8px] text-orange-400 border border-orange-500/30 bg-orange-500/10 px-1 rounded uppercase font-bold">Critical</span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 truncate">
            {task.assigned_to} · {task.vendor}
          </div>
        </div>

        {/* Times */}
        <div className="text-right text-[10px] font-mono w-28 flex-shrink-0">
          <div className="text-slate-500">PLN <span className="text-slate-300">{fmtTime(task.planned_time)}</span></div>
          <div className="text-slate-500">ACT <span className={task.actual_time ? (delayMinutes > 3 ? "text-red-400" : "text-green-400") : "text-slate-600"}>{fmtTime(task.actual_time)}</span></div>
        </div>

        {/* Delay indicator */}
        {delayMinutes !== null && (
          <div className={`w-12 text-right text-[10px] font-mono flex-shrink-0 ${delayMinutes > 3 ? "text-red-400" : "text-green-400"}`}>
            {delayMinutes > 0 ? `+${delayMinutes}m` : delayMinutes < 0 ? `${delayMinutes}m` : "On time"}
          </div>
        )}

        {/* Status badge */}
        <div className={`flex items-center gap-1 px-2 py-1 rounded border text-[9px] font-bold uppercase w-28 justify-center flex-shrink-0 ${cfg.bg} ${cfg.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
          {cfg.label}
        </div>

        <div className="ml-1 text-slate-600">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-3 border-t border-slate-700/30 bg-black/20">
          {task.comments && (
            <div className="text-[11px] text-amber-300/80 mt-2 mb-2 p-2 bg-amber-500/5 rounded border border-amber-500/20">
              {task.comments}
            </div>
          )}
          <div className="flex gap-2 mt-2 flex-wrap">
            {task.status !== "completed" && (
              <button
                onClick={() => handleStatus("completed")}
                disabled={saving}
                className="flex items-center gap-1 text-[10px] font-bold bg-green-500/15 text-green-400 border border-green-500/30 px-2.5 py-1.5 rounded hover:bg-green-500/25 transition disabled:opacity-50"
              >
                {saving ? <Loader size={9} className="animate-spin" /> : <CheckCircle size={9} />}
                Mark Complete
              </button>
            )}
            {task.status === "not_started" && (
              <button
                onClick={() => handleStatus("in_progress")}
                disabled={saving}
                className="flex items-center gap-1 text-[10px] font-bold bg-blue-500/15 text-blue-400 border border-blue-500/30 px-2.5 py-1.5 rounded hover:bg-blue-500/25 transition disabled:opacity-50"
              >
                <Clock size={9} /> Start Task
              </button>
            )}
            {!["completed", "delayed", "blocked"].includes(task.status) && (
              <button
                onClick={() => handleStatus("delayed")}
                disabled={saving}
                className="flex items-center gap-1 text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/30 px-2.5 py-1.5 rounded hover:bg-red-500/20 transition disabled:opacity-50"
              >
                <AlertTriangle size={9} /> Flag Delay
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TurnaroundDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flight, setFlight] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const data = await groundOpsApi.getFlight(id);
      setFlight(data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [id]);

  async function handleTaskUpdate(taskId, update) {
    await groundOpsApi.updateTask(taskId, update);
    load();
  }

  if (loading) return <div className="p-10 text-center text-slate-500 animate-pulse">Loading turnaround...</div>;
  if (!flight) return <div className="p-10 text-center text-red-400">Flight not found</div>;

  const tasks = flight.tasks || [];
  const pct = Math.round(flight.tasks_completed / Math.max(1, flight.tasks_total) * 100);
  const rs = flight.risk_status || "on_time";

  return (
    <div className="max-w-3xl mx-auto p-5">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-orange-400 transition mb-4">
        <ArrowLeft size={12} /> Back to Live Board
      </button>

      <div className="bg-[#0c1929] border border-slate-700/30 rounded-xl p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-black font-mono text-white tracking-tight">{flight.flight_number}</h1>
              <span className="text-slate-400 font-mono text-lg">{flight.origin} → {flight.destination}</span>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              <span className="font-mono text-slate-300">{flight.aircraft_reg}</span>
              <span>·</span>
              <span className="text-orange-400">Stand {flight.stand || "TBA"}</span>
              <span>·</span>
              <span>{flight.aircraft_type}</span>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-black font-mono ${rs === "delayed" || rs === "critical" ? "text-red-400" : rs === "at_risk" ? "text-amber-400" : "text-green-400"}`}>
              {flight.delay_risk_score}
            </div>
            <div className="text-[8px] uppercase text-slate-500 tracking-wider">Delay Risk</div>
            <div className={`text-[10px] font-bold mt-0.5 ${rs === "delayed" ? "text-red-400" : rs === "at_risk" ? "text-amber-400" : "text-green-400"}`}>
              {flight.delay_risk_label}
            </div>
          </div>
        </div>

        {/* Times row */}
        <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-700/30">
          {[
            { label: "Sched Arr", val: new Date(flight.scheduled_arrival).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) },
            { label: "Actual Arr", val: flight.actual_arrival ? new Date(flight.actual_arrival).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : "—" },
            { label: "Dep Target", val: new Date(flight.estimated_departure).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) },
            { label: "Inbound Delay", val: flight.inbound_delay_mins > 0 ? `+${flight.inbound_delay_mins} min` : "On time" },
          ].map(({ label, val }) => (
            <div key={label}>
              <div className="text-[8px] uppercase text-slate-600 tracking-widest">{label}</div>
              <div className="text-sm font-mono font-bold text-white mt-0.5">{val}</div>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div className="mt-4">
          <div className="flex justify-between text-[10px] text-slate-500 mb-1.5">
            <span>{flight.tasks_completed} of {flight.tasks_total} tasks complete</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${RISK_BAR[rs] || "bg-green-500"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Vendors */}
        <div className="flex gap-4 mt-3 text-[10px] text-slate-500 flex-wrap">
          <span>Handler: <span className="text-slate-300">{flight.handling_agent}</span></span>
          <span>Catering: <span className="text-slate-300">{flight.catering_vendor}</span></span>
          <span>Fuel: <span className="text-slate-300">{flight.fueling_vendor}</span></span>
        </div>
      </div>

      {/* ── Task Timeline ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-black uppercase tracking-widest text-slate-400">Turnaround Timeline</h2>
        <span className="text-[9px] text-slate-600 font-mono">Click any task to update</span>
      </div>

      <div className="space-y-2" data-testid="task-timeline">
        {tasks.map(task => (
          <TaskRow key={task.id} task={task} onUpdate={handleTaskUpdate} />
        ))}
      </div>

      {/* ── AI Prediction Panel ────────────────────────────────────────── */}
      <AIPredictionPanel flightId={flight.id} />
    </div>
  );
}
